import ApiService from '../../infrastructure/api/ApiService';
import { TokenStorage } from '../../infrastructure/auth/TokenStorage';
import { exchangeAuthorizationCode, refreshAccessToken } from '../../infrastructure/auth/oauth2Client';
import { generateRandomString, generateCodeChallenge } from '../../infrastructure/auth/pkce';
import {
  OAUTH2_CLIENT_ID,
  OAUTH2_AUTHORIZE_PATH,
  OAUTH2_SCOPE,
  AUTH_CALLBACK_PATH,
} from '../../infrastructure/auth/oauth2Config';
import { API_GATEWAY_ABS_URL } from '../../config/env';

/**
 * getspeakhub-ui's own web login/session (D9) — a second, independent
 * OAuth2 Authorization Code + PKCE flow against api-gw, wholly separate from
 * `/auth/login` (the mobile app's credential bridge, untouched by this file).
 *
 * Mirrors getspeakhub-portal's `core/services/auth.service.ts` at the
 * responsibility level (own the login/callback/refresh/logout orchestration,
 * expose an isAuthenticated signal) — but React-idiomatic: no RxJS
 * BehaviorSubject, just a plain subscribe/notify pair backed by
 * TokenStorage, consumed through ui/hooks/useAuth.ts.
 */

const PKCE_VERIFIER_KEY = 'community_auth_pkce_verifier';
const STATE_KEY = 'community_auth_state';
const RETURN_TO_KEY = 'community_auth_return_to';

function buildRedirectUri(): string {
  return `${window.location.origin}${AUTH_CALLBACK_PATH}`;
}

// A page reload keeps the session (tokens survive in localStorage) but loses
// ApiService's in-memory default header — restore it once at module load so
// the first request after a refresh is already authenticated.
if (TokenStorage.isTokenValid()) {
  const existingToken = TokenStorage.getAccessToken();
  if (existingToken) {
    ApiService.setAuthToken(existingToken);
  }
}

class CommunityAuthService {
  isAuthenticated(): boolean {
    return TokenStorage.isTokenValid();
  }

  /** Notified whenever the session is established or cleared. */
  subscribe(listener: () => void): () => void {
    return TokenStorage.subscribe(listener);
  }

  /**
   * The login trigger (D9): redirects the browser to api-gw's
   * /gw/oauth2/authorize with a PKCE code challenge for the community_web_app
   * client. `returnTo` is where the user lands after a successful callback —
   * defaults to the dashboard itself.
   */
  async beginLogin(returnTo: string = '/my-community'): Promise<void> {
    const codeVerifier = generateRandomString(32);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateRandomString(16);

    sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
    sessionStorage.setItem(STATE_KEY, state);
    sessionStorage.setItem(RETURN_TO_KEY, returnTo);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: OAUTH2_CLIENT_ID,
      redirect_uri: buildRedirectUri(),
      scope: OAUTH2_SCOPE,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    // Full-page, cross-domain navigation — the OAuth2 authorization session
    // (and, for google/apple, the provider handoff) lives on api-gw's own
    // domain. Mirrors AuthLoginPage.tsx's use of API_GATEWAY_ABS_URL for the
    // mobile bridge's `/login` form action, for the identical reason: the
    // session cookie must be set on api-gw's origin, not getspeakhub-ui's.
    window.location.href = `${API_GATEWAY_ABS_URL}/${OAUTH2_AUTHORIZE_PATH}?${params.toString()}`;
  }

  /**
   * Called from /auth/callback once api-gw redirects back with `code` and
   * `state`. Returns the path the caller should navigate to next.
   */
  async completeLogin(code: string, state: string): Promise<string> {
    const expectedState = sessionStorage.getItem(STATE_KEY);
    const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || '/my-community';

    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);

    if (!codeVerifier || !expectedState || expectedState !== state) {
      throw new Error('Sign-in could not be verified. Please try again.');
    }

    const tokenResponse = await exchangeAuthorizationCode(code, buildRedirectUri(), codeVerifier);
    TokenStorage.saveTokens(tokenResponse);
    ApiService.setAuthToken(tokenResponse.access_token);
    return returnTo;
  }

  /** Explicit refresh, for callers outside the 401-retry path (e.g. a foreground poll). */
  async refresh(): Promise<void> {
    const refreshToken = TokenStorage.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    const tokenResponse = await refreshAccessToken(refreshToken);
    TokenStorage.saveTokens(tokenResponse);
    ApiService.setAuthToken(tokenResponse.access_token);
  }

  logout(): void {
    TokenStorage.clearTokens();
    ApiService.clearAuthToken();
  }
}

const authService = new CommunityAuthService();
export default authService;
